import { useEffect, useState, useRef } from 'react';
import { settingsApi, seedApi, notificationsApi, knowledgeBaseApi } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Loader2, Upload, Building2, Save, Database, Mail, Send, Bell, TestTube, User, Quote, BookOpen, Plus, Trash2, ExternalLink, Edit2, X, Check } from 'lucide-react';

export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [smtpSettings, setSmtpSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [testing, setTesting] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [ceoMessage, setCeoMessage] = useState('');
  const [ceoName, setCeoName] = useState('');
  const [ceoTitle, setCeoTitle] = useState('');
  const fileInputRef = useRef(null);
  const ceoImageInputRef = useRef(null);
  
  // Knowledge Base state
  const [knowledgeBaseItems, setKnowledgeBaseItems] = useState([]);
  const [newKbTitle, setNewKbTitle] = useState('');
  const [newKbLink, setNewKbLink] = useState('');
  const [editingKbId, setEditingKbId] = useState(null);
  const [editKbTitle, setEditKbTitle] = useState('');
  const [editKbLink, setEditKbLink] = useState('');

  // SMTP Form state
  const [smtpForm, setSmtpForm] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: 'Performance Portal',
    notifications_enabled: false,
  });

  useEffect(() => {
    loadSettings();
    loadSmtpSettings();
    loadKnowledgeBase();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await settingsApi.get();
      setSettings(response.data);
      setCompanyName(response.data.company_name || '');
      setCeoMessage(response.data.ceo_message || '');
      setCeoName(response.data.ceo_name || '');
      setCeoTitle(response.data.ceo_title || '');
    } catch (error) {
      console.error('Failed to load settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const loadKnowledgeBase = async () => {
    try {
      const response = await knowledgeBaseApi.getAllAdmin();
      setKnowledgeBaseItems(response.data || []);
    } catch (error) {
      console.error('Failed to load knowledge base:', error);
    }
  };

  const handleAddKbItem = async () => {
    if (!newKbTitle.trim() || !newKbLink.trim()) {
      toast.error('Please enter both title and Google Drive link');
      return;
    }
    
    setSaving(true);
    try {
      await knowledgeBaseApi.create({ title: newKbTitle, drive_link: newKbLink });
      toast.success('Knowledge base item added');
      setNewKbTitle('');
      setNewKbLink('');
      loadKnowledgeBase();
    } catch (error) {
      console.error('Failed to add knowledge base item:', error);
      toast.error('Failed to add item');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateKbItem = async (id) => {
    if (!editKbTitle.trim() || !editKbLink.trim()) {
      toast.error('Please enter both title and Google Drive link');
      return;
    }
    
    setSaving(true);
    try {
      await knowledgeBaseApi.update(id, { title: editKbTitle, drive_link: editKbLink });
      toast.success('Knowledge base item updated');
      setEditingKbId(null);
      loadKnowledgeBase();
    } catch (error) {
      console.error('Failed to update knowledge base item:', error);
      toast.error('Failed to update item');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteKbItem = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    setSaving(true);
    try {
      await knowledgeBaseApi.delete(id);
      toast.success('Knowledge base item deleted');
      loadKnowledgeBase();
    } catch (error) {
      console.error('Failed to delete knowledge base item:', error);
      toast.error('Failed to delete item');
    } finally {
      setSaving(false);
    }
  };

  const startEditKbItem = (item) => {
    setEditingKbId(item.id);
    setEditKbTitle(item.title);
    setEditKbLink(item.drive_link);
  };

  const loadSmtpSettings = async () => {
    try {
      const response = await settingsApi.getSmtp();
      setSmtpSettings(response.data);
      setSmtpForm({
        smtp_host: response.data.smtp_host || '',
        smtp_port: response.data.smtp_port || 587,
        smtp_user: response.data.smtp_user || '',
        smtp_password: '',
        smtp_from_email: response.data.smtp_from_email || '',
        smtp_from_name: response.data.smtp_from_name || 'Performance Portal',
        notifications_enabled: response.data.notifications_enabled || false,
      });
    } catch (error) {
      console.error('Failed to load SMTP settings:', error);
    }
  };

  const handleSaveCompanyName = async () => {
    setSaving(true);
    try {
      await settingsApi.update({ company_name: companyName });
      toast.success('Company name updated successfully');
      loadSettings();
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('File size must be less than 2MB');
      return;
    }

    setSaving(true);
    try {
      await settingsApi.uploadLogo(file);
      toast.success('Logo uploaded successfully');
      loadSettings();
    } catch (error) {
      console.error('Failed to upload logo:', error);
      toast.error('Failed to upload logo');
    } finally {
      setSaving(false);
    }
  };

  const handleCeoImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('File size must be less than 2MB');
      return;
    }

    setSaving(true);
    try {
      await settingsApi.uploadCeoImage(file);
      toast.success('CEO image uploaded successfully');
      loadSettings();
    } catch (error) {
      console.error('Failed to upload CEO image:', error);
      toast.error('Failed to upload CEO image');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCeoMessage = async () => {
    setSaving(true);
    try {
      await settingsApi.update({ ceo_message: ceoMessage, ceo_name: ceoName, ceo_title: ceoTitle });
      toast.success('CEO message updated successfully');
      loadSettings();
    } catch (error) {
      console.error('Failed to save CEO message:', error);
      toast.error('Failed to save CEO message');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSMTP = async () => {
    setSaving(true);
    try {
      const dataToSave = { ...smtpForm };
      if (!dataToSave.smtp_password) {
        delete dataToSave.smtp_password;
      }
      await settingsApi.updateSmtp(dataToSave);
      toast.success('SMTP settings updated successfully');
      loadSmtpSettings();
    } catch (error) {
      console.error('Failed to save SMTP settings:', error);
      toast.error('Failed to save SMTP settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestSMTP = async () => {
    if (!testEmail) {
      toast.error('Please enter a test email address');
      return;
    }
    
    setTesting(true);
    try {
      const response = await settingsApi.testSmtp(testEmail);
      if (response.data.sent) {
        toast.success('Test email sent successfully! Check your inbox.');
      } else {
        toast.error(response.data.error || response.data.reason || 'Failed to send test email');
      }
    } catch (error) {
      console.error('Failed to test SMTP:', error);
      toast.error('Failed to send test email');
    } finally {
      setTesting(false);
    }
  };

  const handleSendReminders = async (reviewType) => {
    setSendingReminders(true);
    try {
      const response = await notificationsApi.sendReminders(reviewType, 7);
      toast.success(response.data.message);
    } catch (error) {
      console.error('Failed to send reminders:', error);
      toast.error('Failed to send reminders');
    } finally {
      setSendingReminders(false);
    }
  };

  const handleSeedData = async () => {
    if (!confirm('This will create demo users and competencies. Continue?')) return;
    
    setSeeding(true);
    try {
      const response = await seedApi.seed();
      toast.success(response.data.message);
    } catch (error) {
      console.error('Seed failed:', error);
      toast.error('Failed to seed data');
    } finally {
      setSeeding(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-bold">Settings</h1>
        <p className="text-muted-foreground mt-1">
          Manage application settings, branding, and notifications
        </p>
      </div>

      <Tabs defaultValue="branding" className="space-y-6">
        <TabsList>
          <TabsTrigger value="branding">
            <Building2 className="h-4 w-4 mr-2" />
            Branding
          </TabsTrigger>
          <TabsTrigger value="knowledge-base">
            <BookOpen className="h-4 w-4 mr-2" />
            Knowledge Base
          </TabsTrigger>
          <TabsTrigger value="email">
            <Mail className="h-4 w-4 mr-2" />
            Email Notifications
          </TabsTrigger>
          <TabsTrigger value="data">
            <Database className="h-4 w-4 mr-2" />
            Data Management
          </TabsTrigger>
        </TabsList>

        {/* Branding Tab */}
        <TabsContent value="branding" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Branding
              </CardTitle>
              <CardDescription>
                Customize your organization&apos;s branding
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <Label>Company Logo</Label>
                <div className="flex items-center gap-6">
                  <div className="h-24 w-24 border-2 border-dashed rounded-lg flex items-center justify-center bg-muted/50">
                    {settings?.logo_url ? (
                      <img
                        src={settings.logo_url}
                        alt="Company Logo"
                        className="h-full w-full object-contain rounded-lg"
                      />
                    ) : (
                      <Building2 className="h-8 w-8 text-muted-foreground" />
                    )}
                  </div>
                  <div>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleLogoUpload}
                      accept="image/*"
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={saving}
                      data-testid="upload-logo-btn"
                    >
                      {saving ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Upload className="h-4 w-4 mr-2" />
                      )}
                      Upload Logo
                    </Button>
                    <p className="text-xs text-muted-foreground mt-2">
                      Recommended: 200x200px, PNG or JPG, max 2MB
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="companyName">Company Name</Label>
                <div className="flex gap-3">
                  <Input
                    id="companyName"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="Your Company Name"
                    className="max-w-md"
                    data-testid="company-name-input"
                  />
                  <Button
                    onClick={handleSaveCompanyName}
                    disabled={saving || companyName === settings?.company_name}
                    data-testid="save-company-name-btn"
                  >
                    {saving ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Save className="h-4 w-4 mr-2" />
                    )}
                    Save
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* CEO Message Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Quote className="h-5 w-5" />
                CEO Message
              </CardTitle>
              <CardDescription>
                Display a message from the CEO on the dashboard
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <Label>CEO Photo</Label>
                <div className="flex items-center gap-6">
                  <div className="h-24 w-24 border-2 border-dashed rounded-full flex items-center justify-center bg-muted/50 overflow-hidden">
                    {settings?.ceo_image_url ? (
                      <img
                        src={settings.ceo_image_url}
                        alt="CEO"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <User className="h-8 w-8 text-muted-foreground" />
                    )}
                  </div>
                  <div>
                    <input
                      type="file"
                      ref={ceoImageInputRef}
                      onChange={handleCeoImageUpload}
                      accept="image/*"
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      onClick={() => ceoImageInputRef.current?.click()}
                      disabled={saving}
                      data-testid="upload-ceo-image-btn"
                    >
                      {saving ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Upload className="h-4 w-4 mr-2" />
                      )}
                      Upload CEO Photo
                    </Button>
                    <p className="text-xs text-muted-foreground mt-2">
                      Recommended: Square image, 200x200px, max 2MB
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ceoName">CEO Name</Label>
                <Input
                  id="ceoName"
                  value={ceoName}
                  onChange={(e) => setCeoName(e.target.value)}
                  placeholder="e.g., Ahmad Al-Rashid"
                  className="max-w-md"
                  data-testid="ceo-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="ceoTitle">Title / Designation</Label>
                <Input
                  id="ceoTitle"
                  value={ceoTitle}
                  onChange={(e) => setCeoTitle(e.target.value)}
                  placeholder="e.g., CEO, Herfy Food Services"
                  className="max-w-md"
                  data-testid="ceo-title-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="ceoMessage">Message</Label>
                <Textarea
                  id="ceoMessage"
                  value={ceoMessage}
                  onChange={(e) => setCeoMessage(e.target.value)}
                  placeholder="Enter the CEO's message to display on the dashboard..."
                  className="min-h-[100px]"
                  data-testid="ceo-message-input"
                />
              </div>

              <Button
                onClick={handleSaveCeoMessage}
                disabled={saving || (ceoMessage === settings?.ceo_message && ceoName === settings?.ceo_name && ceoTitle === settings?.ceo_title)}
                data-testid="save-ceo-message-btn"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save CEO Message
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Knowledge Base Tab */}
        <TabsContent value="knowledge-base" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                Knowledge Base
              </CardTitle>
              <CardDescription>
                Manage resources and documentation links shown on the dashboard
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Add New Item Form */}
              <div className="bg-slate-50 rounded-lg p-4 border">
                <h4 className="font-medium text-sm mb-3">Add New Item</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="kbTitle" className="text-xs">Title</Label>
                    <Input
                      id="kbTitle"
                      value={newKbTitle}
                      onChange={(e) => setNewKbTitle(e.target.value)}
                      placeholder="e.g., Employee Handbook"
                      className="mt-1"
                      data-testid="kb-new-title-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="kbLink" className="text-xs">Google Drive Link</Label>
                    <Input
                      id="kbLink"
                      value={newKbLink}
                      onChange={(e) => setNewKbLink(e.target.value)}
                      placeholder="https://drive.google.com/..."
                      className="mt-1"
                      data-testid="kb-new-link-input"
                    />
                  </div>
                </div>
                <div className="flex justify-end mt-3">
                  <Button 
                    onClick={handleAddKbItem} 
                    disabled={saving || !newKbTitle.trim() || !newKbLink.trim()}
                    size="sm"
                    data-testid="kb-add-btn"
                  >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
                    Add Item
                  </Button>
                </div>
              </div>

              {/* Existing Items List */}
              <div>
                <h4 className="font-medium text-sm mb-3">Existing Items ({knowledgeBaseItems.length})</h4>
                {knowledgeBaseItems.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground border rounded-lg bg-slate-50">
                    <BookOpen className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">No knowledge base items yet</p>
                    <p className="text-xs text-gray-400 mt-1">Add your first item above</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {knowledgeBaseItems.map((item) => (
                      <div key={item.id} className="flex items-center gap-3 p-3 border rounded-lg bg-white">
                        {editingKbId === item.id ? (
                          // Edit mode
                          <>
                            <Input
                              value={editKbTitle}
                              onChange={(e) => setEditKbTitle(e.target.value)}
                              className="flex-1 text-sm"
                              placeholder="Title"
                            />
                            <Input
                              value={editKbLink}
                              onChange={(e) => setEditKbLink(e.target.value)}
                              className="flex-1 text-sm"
                              placeholder="Google Drive Link"
                            />
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => handleUpdateKbItem(item.id)}
                              disabled={saving}
                            >
                              <Check className="h-4 w-4 text-green-600" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => setEditingKbId(null)}
                            >
                              <X className="h-4 w-4 text-gray-500" />
                            </Button>
                          </>
                        ) : (
                          // View mode
                          <>
                            <ExternalLink className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{item.title}</p>
                              <p className="text-xs text-muted-foreground truncate">{item.drive_link}</p>
                            </div>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => startEditKbItem(item)}
                              data-testid={`kb-edit-${item.id}`}
                            >
                              <Edit2 className="h-4 w-4 text-blue-600" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => handleDeleteKbItem(item.id)}
                              data-testid={`kb-delete-${item.id}`}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Email Notifications Tab */}
        <TabsContent value="email" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                SMTP Configuration
              </CardTitle>
              <CardDescription>
                Configure email server for sending notifications
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                <div>
                  <Label className="text-base">Enable Email Notifications</Label>
                  <p className="text-sm text-muted-foreground">
                    Send automatic notifications for goals, reviews, and calibration
                  </p>
                </div>
                <Switch
                  checked={smtpForm.notifications_enabled}
                  onCheckedChange={(checked) => setSmtpForm({ ...smtpForm, notifications_enabled: checked })}
                  data-testid="notifications-enabled-switch"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="smtp_host">SMTP Host</Label>
                  <Input
                    id="smtp_host"
                    value={smtpForm.smtp_host}
                    onChange={(e) => setSmtpForm({ ...smtpForm, smtp_host: e.target.value })}
                    placeholder="smtp.example.com"
                    data-testid="smtp-host-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="smtp_port">SMTP Port</Label>
                  <Input
                    id="smtp_port"
                    type="number"
                    value={smtpForm.smtp_port}
                    onChange={(e) => setSmtpForm({ ...smtpForm, smtp_port: parseInt(e.target.value) })}
                    placeholder="587"
                    data-testid="smtp-port-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="smtp_user">SMTP Username</Label>
                  <Input
                    id="smtp_user"
                    value={smtpForm.smtp_user}
                    onChange={(e) => setSmtpForm({ ...smtpForm, smtp_user: e.target.value })}
                    placeholder="user@example.com"
                    data-testid="smtp-user-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="smtp_password">SMTP Password</Label>
                  <Input
                    id="smtp_password"
                    type="password"
                    value={smtpForm.smtp_password}
                    onChange={(e) => setSmtpForm({ ...smtpForm, smtp_password: e.target.value })}
                    placeholder="••••••••"
                    data-testid="smtp-password-input"
                  />
                  <p className="text-xs text-muted-foreground">Leave blank to keep existing password</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="smtp_from_email">From Email</Label>
                  <Input
                    id="smtp_from_email"
                    type="email"
                    value={smtpForm.smtp_from_email}
                    onChange={(e) => setSmtpForm({ ...smtpForm, smtp_from_email: e.target.value })}
                    placeholder="noreply@example.com"
                    data-testid="smtp-from-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="smtp_from_name">From Name</Label>
                  <Input
                    id="smtp_from_name"
                    value={smtpForm.smtp_from_name}
                    onChange={(e) => setSmtpForm({ ...smtpForm, smtp_from_name: e.target.value })}
                    placeholder="Performance Portal"
                    data-testid="smtp-from-name-input"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <Button onClick={handleSaveSMTP} disabled={saving} data-testid="save-smtp-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Save SMTP Settings
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Test Email */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TestTube className="h-5 w-5" />
                Test Email Configuration
              </CardTitle>
              <CardDescription>
                Send a test email to verify your SMTP settings
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-3">
                <Input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="Enter test email address"
                  className="max-w-md"
                  data-testid="test-email-input"
                />
                <Button onClick={handleTestSMTP} disabled={testing || !smtpForm.notifications_enabled} data-testid="send-test-email-btn">
                  {testing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                  Send Test Email
                </Button>
              </div>
              {!smtpForm.notifications_enabled && (
                <p className="text-xs text-muted-foreground mt-2">
                  Enable notifications above to send test emails
                </p>
              )}
            </CardContent>
          </Card>

          {/* Send Reminders */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Send Review Reminders
              </CardTitle>
              <CardDescription>
                Manually send deadline reminders to all employees
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                <Button
                  variant="outline"
                  onClick={() => handleSendReminders('Mid-Year Review')}
                  disabled={sendingReminders || !smtpForm.notifications_enabled}
                  data-testid="send-midyear-reminder-btn"
                >
                  {sendingReminders ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Bell className="h-4 w-4 mr-2" />}
                  Mid-Year Review Reminder
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleSendReminders('Year-End Review')}
                  disabled={sendingReminders || !smtpForm.notifications_enabled}
                  data-testid="send-yearend-reminder-btn"
                >
                  {sendingReminders ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Bell className="h-4 w-4 mr-2" />}
                  Year-End Review Reminder
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleSendReminders('Goal Setting')}
                  disabled={sendingReminders || !smtpForm.notifications_enabled}
                  data-testid="send-goalsetting-reminder-btn"
                >
                  {sendingReminders ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Bell className="h-4 w-4 mr-2" />}
                  Goal Setting Reminder
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Data Management Tab */}
        <TabsContent value="data" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                Demo Data
              </CardTitle>
              <CardDescription>
                Seed the database with sample data for testing
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm">
                    This will create demo users (Admin, HR, Manager, Employee) and default competencies.
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Use this for initial setup or testing purposes.
                  </p>
                </div>
                <Button
                  variant="outline"
                  onClick={handleSeedData}
                  disabled={seeding}
                  data-testid="seed-data-btn"
                >
                  {seeding ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Database className="h-4 w-4 mr-2" />
                  )}
                  Seed Demo Data
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Rating Scale Reference */}
          <Card>
            <CardHeader>
              <CardTitle>Rating Scale</CardTitle>
              <CardDescription>
                Standard 5-point rating scale used for performance evaluation
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[
                  { 
                    score: 5, 
                    label: 'Outstanding', 
                    range: '4.6 - 5.0',
                    target: 'Achieved 105% and above of the Target',
                    description: 'Accomplishments were significantly beyond target performance levels. Contributions to business unit/team and department results were exceptional.',
                    color: 'bg-teal-500'
                  },
                  { 
                    score: 4, 
                    label: 'Exceeds Expectation', 
                    range: '3.8 - 4.5',
                    target: 'Achieved 101% - 104% of the Target',
                    description: 'Accomplishments met and sometimes exceeded target performance levels. All requirements and expectations of the job were fulfilled.',
                    color: 'bg-green-500'
                  },
                  { 
                    score: 3, 
                    label: 'Meets Expectation', 
                    range: '2.7 - 3.7',
                    target: 'Achieved 90% - 100% of the Target',
                    description: 'Accomplishments met and achieve target performance levels. All requirements and expectations of the job were fulfilled.',
                    color: 'bg-blue-400'
                  },
                  { 
                    score: 2, 
                    label: 'Partially Meets Expectation', 
                    range: '1.7 - 2.6',
                    target: 'Achieved 85% - 89% of the Target',
                    description: 'Accomplishments met some but not all target levels and/or basic job requirements were not consistently met. Performance is unsatisfactory and improvement is required.',
                    color: 'bg-yellow-500'
                  },
                  { 
                    score: 1, 
                    label: 'Does Not Meet Expectation', 
                    range: '1.0 - 1.6',
                    target: 'Achieved 84% and below of the Target',
                    description: 'Few objectives were met and basic job requirements were not maintained. Performance improvement must be achieved.',
                    color: 'bg-red-400'
                  },
                ].map((rating) => (
                  <div key={rating.score} className="flex items-start gap-4 p-3 rounded-lg bg-muted/50">
                    <div className={`w-12 h-12 rounded-full flex-shrink-0 flex flex-col items-center justify-center text-white font-bold ${rating.color}`}>
                      <span className="text-lg">{rating.score}</span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium">{rating.label}</p>
                        <span className="text-xs text-muted-foreground">({rating.range})</span>
                      </div>
                      <p className="text-sm font-medium text-primary">{rating.target}</p>
                      <p className="text-sm text-muted-foreground mt-1">{rating.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
